import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  addAddonDefinition,
  fetchAddonDefinitionDetails,
  fetchAllAddOns,
  updateAddonDefinition,
} from '../../common/api/addOnsService';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface AddOnTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface AddonDefinitionDetailsData {
  id: string;
  translations: AddOnTranslations[];
  number: string;
  isRemoved: boolean;
  byPassOwnerPurchase: boolean;
}

export interface AddOnDefinitionItem {
  id: string;
  name: string;
  number: string;
}

export interface AddOnsQueryFilters extends PaginationParameters {
  culture: string;
}

export interface AddOnDetailsQueryFilters {
  id: string;
}

export interface AccommodationAddOnsState {
  addOnsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  addonDefinitionLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  addOnItems: AddOnDefinitionItem[];
  addOnDefinitionDetails: AddonDefinitionDetailsData;
  addonDetailsQueryFilter: AddOnDetailsQueryFilters;
  addonsFilterQueryFilter: AddOnsQueryFilters;
  addonsPagination: PaginationResponse;
}

const initialState: AccommodationAddOnsState = {
  addOnItems: [],
  addOnsStatus: LOADING_STATUS.IDLE,
  addonDefinitionLoadingStatus: LOADING_STATUS.IDLE,
  addonsFilterQueryFilter: {
    culture: 'fi_FI',
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
  },
  addOnDefinitionDetails: {
    id: null,
    translations: [],
    number: null,
    isRemoved: false,
    byPassOwnerPurchase: false,
  },
  addonDetailsQueryFilter: {
    id: null,
  },
  addonsPagination: InitialPaginationResponse,
};

export const updateAddonDefinitionDataAsync = createAsyncThunk(
  'addons/updateAccommodationaddondata',
  async (
    accommodationAddonData: AddonDefinitionDetailsData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAddonDefinition(accommodationAddonData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAddonDefinitionDataAsync = createAsyncThunk(
  'addons/addAccommodationaddondata',
  async (
    accommodationAddonData: AddonDefinitionDetailsData,
    { rejectWithValue },
  ) => {
    try {
      const response = await addAddonDefinition(accommodationAddonData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllAddOnsAsync = createAsyncThunk(
  'addons/getallaccommodationaddons',
  async (filterCriteria: AddOnsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllAddOns(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getAddonDefinitionDetailsAsync = createAsyncThunk(
  'accommodations/getAccommodationDetails',
  async (addonId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAddonDefinitionDetails(addonId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

const accommodationAddonsSlice = createSlice({
  name: 'accommodationattributes',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(getAllAddOnsAsync.fulfilled, (state, action) => {
        state.addOnsStatus = LOADING_STATUS.IDLE;
        state.addOnItems = action.payload.data;
        state.addonsPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllAddOnsAsync.rejected, (state, action) => {
        state.addOnsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllAddOnsAsync.pending, (state, action) => {
        state.addOnsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAddonDefinitionDetailsAsync.rejected, state => {
        state.addonDefinitionLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAddonDefinitionDetailsAsync.pending, state => {
        state.addonDefinitionLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAddonDefinitionDetailsAsync.fulfilled, (state, action) => {
        state.addonDefinitionLoadingStatus = LOADING_STATUS.IDLE;
        state.addOnDefinitionDetails = action.payload;
      });
  },
});

export const selectAddOnsLoadingStatus = (state: RootState) =>
  state.addons.addOnsStatus;
export const selectAddOns = (state: RootState) => state.addons.addOnItems;
export const selectAccommodationAddonDetails = (state: RootState) =>
  state.addons.addOnDefinitionDetails;
export const selectAddOnsPagination = (state: RootState) =>
  state.addons.addonsPagination;
export const selectAccommodationAddOnDetailsLoadingStatus = (
  state: RootState,
) => state.addons.addonDefinitionLoadingStatus;
export default accommodationAddonsSlice.reducer;
